import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";

interface Me {
  user: {
    id: string; email: string; name: string; role: string;
    senderLocalPart: string; senderDisplayName: string | null;
    dailySendCap: number; lastLoginAt: string | null;
  };
}

export default function ProfilePage() {
  const me = useQuery({ queryKey: ["me"], queryFn: () => api<Me>("/api/auth/me") });
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [success, setSuccess] = useState(false);

  const change = useMutation({
    mutationFn: () => api("/api/auth/change-password", {
      method: "POST",
      body: JSON.stringify({ currentPassword: current, newPassword: next }),
    }),
    onSuccess: () => {
      setSuccess(true);
      setCurrent(""); setNext(""); setConfirm("");
    },
  });

  function submit(e: React.FormEvent) {
    e.preventDefault();
    setSuccess(false);
    if (next !== confirm) {
      change.reset();
      return;
    }
    change.mutate();
  }

  if (!me.data) return <div className="p-8">Loading…</div>;
  const u = me.data.user;
  const mismatch = next.length > 0 && confirm.length > 0 && next !== confirm;

  return (
    <div className="p-4 md:p-8 max-w-xl">
      <h1 className="text-2xl font-semibold text-gray-900 mb-1">Your account</h1>
      <p className="text-sm text-gray-500 mb-6">Profile details and password.</p>

      <div className="bg-white border border-gray-200 rounded-xl divide-y divide-gray-100 mb-6">
        <Row label="Login email">{u.email}</Row>
        <Row label="Display name">{u.name}</Row>
        <Row label="Role">{u.role}</Row>
        <Row label="Sender address">{u.senderLocalPart}@notify.wenvia.global</Row>
        <Row label="Reply-To (where replies land)">{u.email}</Row>
        <Row label="Daily send cap">{u.dailySendCap}/day</Row>
        <Row label="Last login">{u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleString() : "—"}</Row>
      </div>
      <p className="text-xs text-gray-500 mb-8">
        Sender address and role can only be changed by an admin (OTP-gated). If you need a different sender, ask
        the workspace admin to update your account.
      </p>

      <form onSubmit={submit} className="bg-white border border-gray-200 rounded-xl p-5">
        <h2 className="font-semibold text-gray-900 mb-3">Change password</h2>
        <label className="block text-xs font-medium text-gray-600 mb-1">Current password</label>
        <input type="password" value={current} onChange={(e) => setCurrent(e.target.value)} required
          className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm mb-3" />
        <label className="block text-xs font-medium text-gray-600 mb-1">New password (≥12 chars)</label>
        <input type="password" value={next} onChange={(e) => setNext(e.target.value)} required minLength={12}
          className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm mb-3" />
        <label className="block text-xs font-medium text-gray-600 mb-1">Confirm new password</label>
        <input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required
          className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm mb-3" />
        {mismatch && <p className="text-sm text-red-600 mb-2">Passwords don't match</p>}
        {change.error && <p className="text-sm text-red-600 mb-2">{(change.error as Error).message}</p>}
        {success && <p className="text-sm text-green-700 mb-2">Password changed.</p>}
        <button type="submit" disabled={change.isPending || !current || !next || !confirm || mismatch}
          className="bg-indigo-600 text-white rounded px-3 py-1.5 text-sm hover:bg-indigo-700 disabled:opacity-50">
          {change.isPending ? "Saving…" : "Change password"}
        </button>
      </form>
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="px-5 py-3 flex items-baseline justify-between gap-4">
      <span className="text-sm text-gray-500 shrink-0">{label}</span>
      <span className="text-sm font-mono text-gray-900 truncate">{children}</span>
    </div>
  );
}
